import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FingerprintIcon, KeyRoundIcon, LogOutIcon, PaletteIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PIN_LENGTH } from '@/components/pin-pad';
import { ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBody, DataRow, SectionTitle } from '@/components/ui/card';
import { Alert, Field, Input, Select } from '@/components/ui/form';
import { Sheet } from '@/components/ui/sheet';
import { authApi } from '@/lib/api/auth';
import {
  biometricCapability,
  biometricUnlocksCache,
  disableBiometric,
  enableBiometric,
} from '@/lib/auth/biometric';
import { useAuth } from '@/lib/auth/context';
import { CHANGELOG } from '@/lib/changelog';
import { dateTimeId, relativeId } from '@/lib/format';
import { patchEnvelope, sessionSummary } from '@/lib/session';
import { setTheme, THEME_LABELS, THEMES, useTheme } from '@/lib/theme';
import { useOnline } from '@/lib/use-online';
import { replacePin } from '@/offline/crypto';
import { APP_BUILT_AT, APP_VERSION } from '@/sw/register';

export const Route = createFileRoute('/_app/akun')({
  component: AccountPage,
});

/**
 * Halaman Akun (T-P7-28/29/02, FR-AUTH-09).
 *
 * Tiga hal yang mudah tercecer dikumpulkan di sini karena semuanya dicari di
 * tempat yang sama: identitas & keamanan perangkat, tampilan layar, dan versi
 * aplikasi. Yang terakhir bukan hiasan — pertanyaan pertama saat melaporkan
 * masalah dari lapangan selalu "versi berapa".
 */
function AccountPage() {
  const { user, envelope, signOut } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();
  const [theme] = useTheme();

  const [summary, setSummary] = useState<Awaited<ReturnType<typeof sessionSummary>> | null>(null);
  const [biometricOn, setBiometricOn] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [sheet, setSheet] = useState<'pin' | 'password' | 'biometric' | 'logout' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void sessionSummary().then(setSummary);
    void biometricUnlocksCache().then(setBiometricOn);
    void biometricCapability().then((capability) =>
      setBiometricSupported(capability.supported && capability.platformAuthenticator),
    );
  }, []);

  if (!user) return <ListSkeleton rows={3} />;

  return (
    <div className="space-y-4 px-4 py-4">
      {notice ? <Alert variant="success">{notice}</Alert> : null}

      <Card>
        <CardBody>
          <dl className="divide-y divide-border">
            <DataRow label="Nama" value={user.fullName} />
            <DataRow label="Username" value={user.username} />
            <DataRow label="Peran" value={user.role} />
            <DataRow label="Cabang" value={envelope?.branchName ?? '—'} />
            <DataRow label="Sinkron terakhir" value={relativeId(summary?.lastSyncAt)} />
            <DataRow
              label="Menunggu kirim"
              value={summary ? `${summary.pendingOutbox} data · ${summary.pendingMedia} foto` : '—'}
            />
          </dl>
        </CardBody>
      </Card>

      <SectionTitle>Keamanan perangkat</SectionTitle>

      <div className="space-y-2">
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setSheet('pin')}
          leading={<KeyRoundIcon className="size-4" aria-hidden="true" />}
        >
          Ganti PIN perangkat
        </Button>

        <Button
          variant="secondary"
          fullWidth
          disabled={!online}
          onClick={() => setSheet('password')}
          leading={<KeyRoundIcon className="size-4" aria-hidden="true" />}
        >
          Ganti password{!online ? ' (perlu koneksi)' : ''}
        </Button>

        {biometricSupported ? (
          <Button
            variant="secondary"
            fullWidth
            disabled={!online && !biometricOn}
            onClick={() => setSheet('biometric')}
            leading={<FingerprintIcon className="size-4" aria-hidden="true" />}
          >
            {biometricOn ? 'Matikan biometrik' : 'Aktifkan biometrik'}
            {biometricOn ? <Badge tone="success">Aktif</Badge> : null}
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">
            Perangkat ini tidak memiliki sensor biometrik yang didukung peramban. PIN tetap
            berfungsi penuh.
          </p>
        )}
      </div>

      <SectionTitle>Tampilan</SectionTitle>

      <Field id="theme" label="Tema layar" hint="Mode sinar matahari menaikkan kontras maksimum.">
        <Select
          id="theme"
          value={theme}
          onChange={(event) => setTheme(event.target.value as (typeof THEMES)[number])}
        >
          {THEMES.map((value) => (
            <option key={value} value={value}>
              {THEME_LABELS[value]}
            </option>
          ))}
        </Select>
      </Field>

      <SectionTitle>Tentang aplikasi</SectionTitle>

      <Card>
        <CardBody className="space-y-3">
          <dl className="divide-y divide-border">
            <DataRow label="Versi" value={APP_VERSION} />
            <DataRow label="Build" value={dateTimeId(APP_BUILT_AT)} />
          </dl>

          {CHANGELOG.map((entry) => (
            <div key={entry.version} className="space-y-1">
              <p className="text-sm font-medium">
                <PaletteIcon className="mr-1 inline size-3.5" aria-hidden="true" />
                Versi {entry.version}
              </p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {entry.highlights.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ))}
        </CardBody>
      </Card>

      <Button
        variant="danger"
        size="lg"
        fullWidth
        onClick={() => setSheet('logout')}
        leading={<LogOutIcon className="size-4" aria-hidden="true" />}
      >
        Keluar & hapus data perangkat
      </Button>

      <ChangePinSheet
        open={sheet === 'pin'}
        onClose={() => setSheet(null)}
        onDone={() => {
          setSheet(null);
          setNotice('PIN perangkat diperbarui.');
        }}
      />

      <ChangePasswordSheet
        open={sheet === 'password'}
        onClose={() => setSheet(null)}
        onDone={() => {
          setSheet(null);
          setNotice('Password diganti. Masuk kembali pada perangkat lain bila perlu.');
        }}
      />

      <BiometricSheet
        open={sheet === 'biometric'}
        enabled={biometricOn}
        deviceId={envelope?.deviceId ?? null}
        onClose={() => setSheet(null)}
        onDone={async (enabled) => {
          setBiometricOn(enabled);
          await patchEnvelope({ biometricEnabled: enabled });
          setSheet(null);
          setNotice(enabled ? 'Biometrik aktif di perangkat ini.' : 'Biometrik dimatikan.');
        }}
      />

      <Sheet
        open={sheet === 'logout'}
        onClose={() => setSheet(null)}
        title="Keluar dari aplikasi"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" fullWidth onClick={() => setSheet(null)}>
              Batal
            </Button>
            <Button
              variant="danger"
              fullWidth
              onClick={async () => {
                await signOut();
                await navigate({ to: '/masuk', replace: true });
              }}
            >
              Keluar & hapus
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm">
            Logout menghapus seluruh data nasabah dari perangkat ini, termasuk antrean yang belum
            terkirim.
          </p>

          {summary && summary.pendingOutbox + summary.pendingMedia > 0 ? (
            <Alert variant="danger">
              Masih ada {summary.pendingOutbox} data dan {summary.pendingMedia} foto yang belum
              terkirim. Sinkronkan lebih dulu — data ini tidak dapat dipulihkan setelah dihapus.
            </Alert>
          ) : null}
        </div>
      </Sheet>
    </div>
  );
}

function ChangePinSheet({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);

    if (next.length !== PIN_LENGTH) return setError(`PIN baru harus ${PIN_LENGTH} digit.`);
    if (next !== confirm) return setError('Konfirmasi PIN tidak sama.');

    setBusy(true);
    const changed = await replacePin(current, next);
    setBusy(false);

    if (!changed) return setError('PIN lama salah.');

    setCurrent('');
    setNext('');
    setConfirm('');
    onDone();
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Ganti PIN perangkat"
      footer={
        <Button fullWidth disabled={busy} onClick={() => void submit()}>
          Simpan PIN baru
        </Button>
      }
    >
      <div className="space-y-4">
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <p className="text-sm text-muted-foreground">
          PIN membuka data yang tersimpan di perangkat ini. Menggantinya tidak memengaruhi password
          akun Anda.
        </p>

        <PinField id="pin-current" label="PIN saat ini" value={current} onChange={setCurrent} />
        <PinField id="pin-next" label="PIN baru" value={next} onChange={setNext} />
        <PinField id="pin-confirm" label="Ulangi PIN baru" value={confirm} onChange={setConfirm} />
      </div>
    </Sheet>
  );
}

function PinField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field id={id} label={label} required>
      <Input
        id={id}
        type="password"
        inputMode="numeric"
        autoComplete="off"
        maxLength={PIN_LENGTH}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
      />
    </Field>
  );
}

function ChangePasswordSheet({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);
    setBusy(true);

    try {
      await authApi.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Password gagal diganti.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Ganti password"
      footer={
        <Button fullWidth disabled={busy} onClick={() => void submit()}>
          Simpan password
        </Button>
      }
    >
      <div className="space-y-4">
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Field id="current-password" label="Password saat ini" required>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </Field>

        <Field
          id="new-password"
          label="Password baru"
          required
          hint="Minimal 8 karakter dengan huruf besar, huruf kecil, dan angka."
        >
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </Field>
      </div>
    </Sheet>
  );
}

function BiometricSheet({
  open,
  enabled,
  deviceId,
  onClose,
  onDone,
}: {
  open: boolean;
  enabled: boolean;
  deviceId: string | null;
  onClose: () => void;
  onDone: (enabled: boolean) => Promise<void>;
}) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);

    if (!deviceId) {
      setError('Perangkat belum terdaftar. Sinkronkan lebih dulu, lalu ulangi.');
      return;
    }

    setBusy(true);

    try {
      if (enabled) {
        await disableBiometric(deviceId);
        await onDone(false);
        return;
      }

      const result = await enableBiometric(deviceId, pin);
      if (!result.unlocksCache) {
        setError(
          'Biometrik terdaftar untuk login, tetapi peramban ini tidak mendukung pembukaan cache offline lewat biometrik. PIN tetap diperlukan saat offline.',
        );
      }

      await onDone(result.unlocksCache);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pendaftaran biometrik gagal.');
    } finally {
      setBusy(false);
      setPin('');
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={enabled ? 'Matikan biometrik' : 'Aktifkan biometrik'}
      footer={
        <Button
          fullWidth
          variant={enabled ? 'danger' : 'primary'}
          disabled={busy || (!enabled && pin.length !== PIN_LENGTH)}
          onClick={() => void submit()}
        >
          {enabled ? 'Matikan' : 'Aktifkan'}
        </Button>
      }
    >
      <div className="space-y-4">
        {error ? <Alert variant="warning">{error}</Alert> : null}

        {enabled ? (
          <p className="text-sm">
            Biometrik akan dicabut dari perangkat ini. Anda tetap dapat masuk dengan username,
            password, dan PIN.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              PIN saat ini diperlukan sekali: kunci data perangkat dibungkus ulang agar sidik jari
              dapat membukanya tanpa membuat kunci baru.
            </p>
            <PinField id="bio-pin" label="PIN saat ini" value={pin} onChange={setPin} />
          </>
        )}
      </div>
    </Sheet>
  );
}
